import { DragDropContext, Droppable, DropResult } from '@hello-pangea/dnd';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { ISelectOption } from '../../../../../../../../design/inputs/select/ISelectOption';
import { GLOBAL_NONCE } from '../../../../../../../../ThemeProvider';
import { SortingDirection } from '../../../../../../../models/sortingDirection.model';
import { ISorting, SortingBy } from '../../../imageFilters.model';
import { FilterContent } from '../FilterContent';
import { ImageFiltersFormProps } from '../ImageFiltersForm';
import { SortingRowForm } from './SortingRowForm';

export const SortingBody = observer((props: ImageFiltersFormProps): ReactElement => {
  const { t } = useTranslator();

  const handleSortingChanged = (sorting: ISorting): void => {
    const newArray = props.imageFilters.sorting.slice();
    const index = newArray.findIndex((x) => x.order === sorting.order);
    if (index > -1) {
      newArray.splice(index, 1, sorting);
      props.onSetSorting(newArray);
    }
  };

  const handleAddAnother = (): void => {
    const topOrder = props.imageFilters.sorting.map((x) => x.order).reduce((x, y) => (x > y ? x : y), 0);
    const newSorting = props.imageFilters.sorting.concat({
      by: undefined,
      direction: SortingDirection.ASC,
      order: topOrder + 1,
    });
    props.onSetSorting(newSorting);
  };

  const handleDelete = (order: number): void => {
    const newSorting = props.imageFilters.sorting
      .filter((x) => x.order !== order)
      .sort((a, b) => a.order - b.order)
      .map((a, i) => ({ ...a, order: i }));

    props.onSetSorting(newSorting);
  };

  const handleDragEnd = (result: DropResult): void => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const [removed] = props.imageFilters.sorting.splice(result.source.index, 1);
    props.imageFilters.sorting.splice(result.destination.index, 0, removed);
    const newSorting = props.imageFilters.sorting.map((a, i) => ({ ...a, order: i }));

    props.onSetSorting(newSorting);
  };

  const allOptions: ISelectOption<SortingBy>[] = Object.values(SortingBy)
    .filter((x) => x !== SortingBy.None)
    .map((o) => ({ label: t(`sorting_by.${o}`), value: o }));

  return (
    <DragDropContext
      onDragEnd={handleDragEnd}
      nonce={GLOBAL_NONCE}
    >
      <FilterContent>
        <Droppable droppableId='droppable'>
          {(provided): ReactElement => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className='droppable-container'
            >
              {props.imageFilters.sorting.map((x) => {
                const options = allOptions.filter((a) => !props.imageFilters.sorting.filter((s) => s.order !== x.order).some((x) => x.by === a.value));
                return (
                  <SortingRowForm
                    key={x.order}
                    order={x.order}
                    sorting={x}
                    options={options}
                    onChange={handleSortingChanged}
                    onDelete={handleDelete}
                  />
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <div>
          <Button onClick={handleAddAnother}>+ {t('add_another')} </Button>
        </div>
      </FilterContent>
    </DragDropContext>
  );
});
